/**
 * CommsRail — the project's live "what is everybody doing" column.
 *
 * Renders EXACTLY what GET /projects/:id/events returns (contract:
 * plans/2026-08-27-comms-rail-api-contract.md): the server writes the
 * sentence, the meta links and the running/needs-you rollups; this component
 * lays them out. No narration here, no per-kind branches.
 *
 * Props
 *   projectId   string
 *   agents      the /projects/:id/workflows rows (uuid, workflowType,
 *               displayName, iconUrl) — drives the picker + run-link routing
 *   scopeUuids  string[] | null — restrict to these agents (agent page passes
 *               itself + members); null = whole project
 *   title       header label (project: "COMMS"; agent page: the agent's name)
 *   open        boolean — closed = nothing rendered, nothing polled
 *   onClose     () => void (Esc + the ✕ button)
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { IonIcon } from '@ionic/react';
import { closeOutline } from 'ionicons/icons';
import { useQueryClient } from '@tanstack/react-query';
import LetterAvatar from '../../atoms/LetterAvatar/LetterAvatar';
import { useToast } from '../../../contexts/ToastContext';
import { useProjectEvents } from '../../../hooks/useProjectEvents';
import { actOnProjectEvent } from '../../../services/projectEvents';
import {
  renderEventText, eventTimeLabel, eventHref,
  deckItems, deckIndex, deckStep, deckAdvance, ageLabel, cardFacts,
} from '../../../utils/projectEvents';
import './CommsRail.css';

// Same fallback rule as the Agents list `WorkflowIcon` / detail `WorkflowDetailIcon`:
// the real marketplace icon when there is one, LetterAvatar only when there is
// none or it 404s. Never initials over a real icon.
function AgentIcon({ iconUrl, label, className = '' }) {
  const [failed, setFailed] = useState(false);
  useEffect(() => { setFailed(false); }, [iconUrl]);
  if (!iconUrl || failed) return <LetterAvatar name={label} className={className} />;
  return <img src={iconUrl} onError={() => setFailed(true)} className={className} alt="" loading="lazy" />;
}
AgentIcon.propTypes = { iconUrl: PropTypes.string, label: PropTypes.string, className: PropTypes.string };

const agentLabel = (a) => a?.displayName || a?.workflowType || '';

function MetaItem({ item, projectId, agents }) {
  const tone = ['ok', 'bad', 'warn'].includes(item?.tone) ? ` comms-meta--${item.tone}` : '';
  const link = eventHref(item?.href, projectId, agents);
  if (!link) return <span className={`comms-meta-item${tone}`}>{item?.label}</span>;
  if (link.external) {
    return (
      <a className={`comms-meta-item comms-meta-link${tone}`} href={link.href} target="_blank" rel="noopener noreferrer">
        {item.label}
      </a>
    );
  }
  return <Link className={`comms-meta-item comms-meta-link${tone}`} to={link.href}>{item.label}</Link>;
}
MetaItem.propTypes = { item: PropTypes.object, projectId: PropTypes.string, agents: PropTypes.array };

function EventRow({ ev, projectId, agents, now }) {
  const segments = useMemo(() => renderEventText(ev.text), [ev.text]);
  const label = ev.agent?.displayName || ev.agent?.workflowType || '';
  return (
    <li className={`comms-ev${ev.running ? ' comms-ev--running' : ''}`} data-kind={ev.kind}>
      <span className="comms-ev-who" title={label}>
        <AgentIcon iconUrl={ev.agent?.iconUrl || null} label={label} className="comms-ev-icon" />
      </span>
      <div className="comms-ev-body">
        <p className="comms-ev-text">
          {segments.map((s, i) => (s.bold
            ? <strong key={i} className="comms-ev-bold">{s.text}</strong>
            : <React.Fragment key={i}>{s.text}</React.Fragment>))}
          {ev.running && <span className="comms-ev-cursor" aria-hidden="true" />}
        </p>
        <div className="comms-ev-meta">
          <time className="comms-meta-item comms-meta-time" dateTime={ev.at}>
            {eventTimeLabel(ev.at, now, !!ev.running)}
          </time>
          {(Array.isArray(ev.meta) ? ev.meta : []).map((m, i) => (
            <MetaItem key={`${m?.label || ''}-${i}`} item={m} projectId={projectId} agents={agents} />
          ))}
        </div>
      </div>
    </li>
  );
}
EventRow.propTypes = {
  ev: PropTypes.object.isRequired, projectId: PropTypes.string, agents: PropTypes.array, now: PropTypes.number,
};

const RELEASE_TITLE = "Remove the ai:blocked label — the ticket goes straight back into the fleet's pool";
const TAKE_OVER_TITLE = 'Add no-bundle and remove the consent label (ai:ready / ai-fix) — the fleet leaves this ticket to a human from now on';
const isHttps = (u) => typeof u === 'string' && /^https:\/\//.test(u);

/**
 * NeedsYouDeck — ONE card at a time out of `needsYou.items` (the server
 * derives the cards; the client only turns them). ‹ i/n › top-right, ←/→
 * rotate (wired by the rail), a card leaves the deck the moment its action
 * is recorded. No motion — the card just changes. Nothing rendered when
 * there is nothing to decide.
 */
function NeedsYouDeck({ items, projectId, now, onRecorded, stepRef }) {
  // Where the deck points: follow the SAME card across polls (by eventId),
  // else clamp the position. Cards acted on locally leave the deck at once.
  const [pos, setPos] = useState({ idx: 0, eventId: null });
  const [acted, setActed] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const [answer, setAnswer] = useState('');
  const { showToast } = useToast();

  const visible = useMemo(() => items.filter((i) => !acted.has(i.eventId)), [items, acted]);
  const n = visible.length;
  const idx = deckIndex(visible, pos.idx, pos.eventId);
  const item = visible[idx] || null;

  const step = useCallback((dir) => {
    if (n < 2) return;
    const next = deckStep(idx, n, dir);
    setPos({ idx: next, eventId: visible[next].eventId });
  }, [idx, n, visible]);
  // The rail owns the ←/→ listener (focus must be inside it); it reaches the
  // deck through this ref so there is one keyboard handler, not two.
  useEffect(() => { stepRef.current = step; return () => { stepRef.current = null; }; }, [step, stepRef]);

  // A different card on screen → the half-typed answer belongs to the old one.
  const shownId = item ? item.eventId : null;
  useEffect(() => { setAnswer(''); }, [shownId]);

  const act = async (action) => {
    if (busy || !item) return;
    const body = { action };
    if (action === 'answer') {
      body.text = answer.trim();
      if (!body.text) { showToast('Write the answer first', 'warning'); return; }
    }
    const { eventId, ticketKey } = item;
    setBusy(true);
    try {
      const res = await actOnProjectEvent(projectId, eventId, body);
      const adv = deckAdvance(visible, idx, eventId);
      setActed((prev) => new Set(prev).add(eventId));
      setPos({ idx: adv.index, eventId: adv.items[adv.index] ? adv.items[adv.index].eventId : null });
      if (res && res.event && res.event.id) onRecorded(res.event);
      showToast(
        action === 'answer' ? `Answered ${ticketKey} — posted on the ticket, the agent picks it up next tick`
          : action === 'release' ? `Released ${ticketKey} — ai:blocked removed, back in the pool`
            : `Took over ${ticketKey} — no-bundle on, consent off, the fleet leaves it alone`,
        'success',
      );
    } catch (e) {
      const verb = action === 'answer' ? 'answer' : action === 'release' ? 'release' : 'take over';
      showToast(`Couldn't ${verb} ${ticketKey}${e && e.message ? ` — ${e.message}` : ''}`, 'error');
    } finally {
      setBusy(false);
    }
  };

  if (!item) return null;
  const blocked = item.type === 'blocked';
  const who = item.agent || null;
  const whoLabel = agentLabel(who);
  const facts = cardFacts(item);
  const open = isHttps(item.url) && (
    <a className="comms-card-open" href={item.url} target="_blank" rel="noopener noreferrer">Open ticket ↗</a>
  );

  return (
    <section
      className={`comms-deck${blocked ? ' comms-deck--blocked' : ''}${busy ? ' comms-deck--busy' : ''}`}
      aria-label="Needs you"
      data-testid="comms-needs-you"
      data-event-id={item.eventId}
      data-ticket={item.ticketKey}
    >
      <div className="comms-deck-hd">
        <span>{`NEEDS YOU · ${n}`}</span>
        <span className="comms-deck-nav" aria-label="Rotate">
          <button type="button" onClick={() => step(-1)} aria-label="Previous" disabled={n < 2}>‹</button>
          <span className="comms-deck-n">{`${idx + 1}/${n}`}</span>
          <button type="button" onClick={() => step(1)} aria-label="Next" disabled={n < 2}>›</button>
        </span>
      </div>
      <div className="comms-card-who">
        {who && (
          <span className="comms-ev-who" title={whoLabel}>
            <AgentIcon iconUrl={who.iconUrl || null} label={whoLabel} className="comms-ev-icon" />
          </span>
        )}
        <span className="comms-card-key">{item.ticketKey}</span>
        <span className={`comms-chip${blocked ? ' comms-chip--blocked' : ''}`}>{blocked ? 'BLOCKED' : 'QUESTION'}</span>
        {who && <span className="comms-card-by">{whoLabel}</span>}
        <time className="comms-card-age" dateTime={item.at || undefined}>{ageLabel(item.at, now)}</time>
      </div>
      <p className="comms-card-why">
        {item.text || (blocked ? 'No reason was written on the ticket.' : 'No question was written on the ticket.')}
      </p>
      {facts.length > 0 && (
        <div className="comms-card-facts">
          {facts.map((f) => (f.href
            ? <a key={f.label} className={f.tone === 'bad' ? 'comms-fact--bad' : undefined} href={f.href} target="_blank" rel="noopener noreferrer">{f.label}</a>
            : <span key={f.label} className={f.tone === 'bad' ? 'comms-fact--bad' : undefined}>{f.label}</span>))}
        </div>
      )}
      {blocked ? (
        <div className="comms-card-acts">
          <button type="button" className="comms-btn" onClick={() => act('release')} title={RELEASE_TITLE} disabled={busy}>Release</button>
          <button type="button" className="comms-btn comms-btn--sec" onClick={() => act('take_over')} title={TAKE_OVER_TITLE} disabled={busy}>Take over</button>
          {open}
        </div>
      ) : (
        <div className="comms-card-answer">
          <textarea
            rows={3}
            value={answer}
            onChange={(e) => setAnswer(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); act('answer'); } }}
            placeholder="Answer in your own words — it is posted as a comment on the ticket"
            aria-label="Your answer"
            disabled={busy}
          />
          <div className="comms-card-acts">
            <button type="button" className="comms-btn" onClick={() => act('answer')} disabled={busy}>Answer</button>
            <span className="comms-card-hint">{`${whoLabel || 'The agent'} picks it up on the next tick`}</span>
            {open}
          </div>
        </div>
      )}
    </section>
  );
}
NeedsYouDeck.propTypes = {
  items: PropTypes.array.isRequired,
  projectId: PropTypes.string,
  now: PropTypes.number,
  onRecorded: PropTypes.func.isRequired,
  stepRef: PropTypes.shape({ current: PropTypes.any }).isRequired,
};

export default function CommsRail({
  projectId, agents = [], scopeUuids = null, title = 'COMMS', open = true, onClose,
}) {
  // Picker: null = all agents in scope; a uuid narrows to one.
  const [picked, setPicked] = useState(null);
  const pickerAgents = useMemo(() => {
    const list = Array.isArray(agents) ? agents.filter((a) => a && a.uuid) : [];
    if (!Array.isArray(scopeUuids) || !scopeUuids.length) return list;
    const set = new Set(scopeUuids);
    return list.filter((a) => set.has(a.uuid));
  }, [agents, scopeUuids]);
  useEffect(() => {
    if (picked && !pickerAgents.some((a) => a.uuid === picked)) setPicked(null);
  }, [picked, pickerAgents]);

  const effectiveScope = picked ? [picked] : scopeUuids;
  const {
    events, runningCount, needsYou, queryKey, isLoading, isError, hasNextPage, fetchNextPage, isFetchingNextPage,
  } = useProjectEvents(projectId, { scopeUuids: effectiveScope, enabled: open });

  // NEEDS YOU deck: the server's cards; a recorded human_* event goes straight
  // onto page 1 of the cache (mergeEventPages dedupes when the poll brings it
  // back), then the query is refetched so the deck itself catches up.
  const queryClient = useQueryClient();
  const deck = useMemo(() => deckItems(needsYou), [needsYou]);
  const stepRef = useRef(null);
  const onRecorded = useCallback((event) => {
    if (!queryKey) return;
    queryClient.setQueryData(queryKey, (old) => {
      if (!old || !Array.isArray(old.pages) || !old.pages.length) return old;
      const first = old.pages[0] || {};
      const rest = Array.isArray(first.events) ? first.events.filter((e) => e && e.id !== event.id) : [];
      return { ...old, pages: [{ ...first, events: [event, ...rest] }, ...old.pages.slice(1)] };
    });
    queryClient.invalidateQueries({ queryKey });
  }, [queryClient, queryKey]);
  // ←/→ rotate the deck while focus is inside the rail and not in a text field.
  const onRailKeyDown = (e) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    const tag = e.target && e.target.tagName;
    if (tag === 'TEXTAREA' || tag === 'INPUT' || tag === 'SELECT') return;
    if (!stepRef.current) return;
    e.preventDefault();
    stepRef.current(e.key === 'ArrowRight' ? 1 : -1);
  };

  // Esc closes.
  useEffect(() => {
    if (!open || !onClose) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Infinite scroll: a sentinel at the bottom of the list asks for the next page.
  const sentinelRef = useRef(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!open || !el || !hasNextPage || typeof IntersectionObserver === 'undefined') return undefined;
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting) && !isFetchingNextPage) fetchNextPage();
    }, { rootMargin: '120px' });
    io.observe(el);
    return () => io.disconnect();
  }, [open, hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Relative-time labels tick once a minute (the list itself re-renders on poll).
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!open) return undefined;
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(id);
  }, [open, events]);

  if (!open) return null;

  const tickerText = events.length
    ? renderEventText(events[0].text).map((s) => s.text).join('')
    : 'Waiting for the first event';

  return (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <aside className="comms-rail" aria-label={`${title} activity`} onKeyDown={onRailKeyDown}>
      <header className="comms-hd">
        <span className={`comms-dot${runningCount > 0 ? ' comms-dot--live' : ''}`} aria-hidden="true" />
        <b className="comms-hd-title">{title}</b>
        <span className="comms-hd-n" aria-live="polite">{runningCount} running</span>
        {onClose && (
          <button type="button" className="comms-close" onClick={onClose} aria-label="Close activity rail">
            <IonIcon icon={closeOutline} />
          </button>
        )}
      </header>

      {deck.length > 0 && (
        <NeedsYouDeck items={deck} projectId={projectId} now={now} onRecorded={onRecorded} stepRef={stepRef} />
      )}

      {pickerAgents.length > 1 && (
        <div className="comms-picker" role="tablist" aria-label="Filter by agent">
          <button
            type="button"
            role="tab"
            aria-selected={!picked}
            className={`comms-pick${!picked ? ' comms-pick--on' : ''}`}
            onClick={() => setPicked(null)}
          >
            All
          </button>
          {pickerAgents.map((a) => (
            <button
              key={a.uuid}
              type="button"
              role="tab"
              aria-selected={picked === a.uuid}
              className={`comms-pick${picked === a.uuid ? ' comms-pick--on' : ''}`}
              onClick={() => setPicked(picked === a.uuid ? null : a.uuid)}
              title={agentLabel(a)}
            >
              <AgentIcon iconUrl={a.iconUrl || null} label={agentLabel(a)} className="comms-pick-icon" />
              <span className="comms-pick-label">{agentLabel(a)}</span>
            </button>
          ))}
        </div>
      )}

      <div className="comms-list-wrap">
        {isLoading ? (
          <ul className="comms-list" aria-busy="true" aria-label="Loading activity">
            {[0, 1, 2, 3].map((i) => (
              <li key={i} className="comms-ev comms-ev--skel">
                <span className="comms-ev-who comms-skel" />
                <div className="comms-ev-body">
                  <div className="comms-skel comms-skel--line" />
                  <div className="comms-skel comms-skel--meta" />
                </div>
              </li>
            ))}
          </ul>
        ) : isError ? (
          <p className="comms-empty" role="alert">Could not load activity.</p>
        ) : events.length === 0 ? (
          <p className="comms-empty">Nothing has happened in this project yet</p>
        ) : (
          <ul className="comms-list">
            {events.map((ev) => (
              <EventRow key={ev.id} ev={ev} projectId={projectId} agents={agents} now={now} />
            ))}
          </ul>
        )}
        <div ref={sentinelRef} className="comms-sentinel" aria-hidden="true">
          {isFetchingNextPage ? 'loading older…' : ''}
        </div>
      </div>

      <footer className="comms-tick" aria-hidden="true">
        <span>{`${runningCount} running · ${tickerText}`}</span>
      </footer>
    </aside>
  );
}

CommsRail.propTypes = {
  projectId: PropTypes.string,
  agents: PropTypes.array,
  scopeUuids: PropTypes.arrayOf(PropTypes.string),
  title: PropTypes.string,
  open: PropTypes.bool,
  onClose: PropTypes.func,
};

/**
 * useCommsRailOpen — the rail's open/closed state, remembered per browser in
 * localStorage `comms-rail-open` ('1' | '0'). Default closed.
 */
export const COMMS_RAIL_OPEN_KEY = 'comms-rail-open';
export function useCommsRailOpen() {
  const [open, setOpen] = useState(() => {
    try { return window.localStorage.getItem(COMMS_RAIL_OPEN_KEY) === '1'; } catch { return false; }
  });
  useEffect(() => {
    try { window.localStorage.setItem(COMMS_RAIL_OPEN_KEY, open ? '1' : '0'); } catch { /* private mode */ }
  }, [open]);
  return [open, setOpen];
}
